import { Link } from 'react-router-dom';
import { MapPin, Calendar, Clock, CheckCircle2, XCircle, AlertCircle, ArrowRight, Store, ExternalLink } from 'lucide-react';
import { useMyFranchiseOffers } from '@/hooks/useOffer';
import { useAuthStore } from '@/store/authStore';
import dayjs from '@/lib/dayjs';
import { formatNumber } from '@/utils';

function formatPrice(str: string) {
  if (!str) return 'Thỏa thuận';
  const n = parseFloat(str);
  if (isNaN(n)) return str;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} tỷ`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} triệu`;
  return formatNumber(n);
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, any> = {
    accepted: { label: 'Đã đồng ý', icon: <CheckCircle2 size={12} />, bg: '#dcfce7', color: '#16a34a' },
    rejected: { label: 'Bị từ chối', icon: <XCircle size={12} />, bg: '#fee2e2', color: '#dc2626' },
    pending: { label: 'Đang chờ xử lý', icon: <AlertCircle size={12} />, bg: '#fef9c3', color: '#ca8a04' },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: s.bg, color: s.color }}>
      {s.icon} {s.label}
    </span>
  );
}

function OfferCard({ item }: { item: any }) {
  const Franchise = item.entityData || item.franchise || {};

  const fullAddr = [
    Franchise.streetAddress,
    Franchise.ward?.name,
    Franchise.district?.name,
    Franchise.province?.name,
  ].filter(Boolean).join(', ');

  return (
    <div className="fnb-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <Link to={`/nhuong-quyen/${Franchise.slug || Franchise.id || item.entityId}`} className="font-bold text-base hover:text-orange-500 transition-colors line-clamp-1" style={{ color: 'var(--text-1)' }}>
            {Franchise.title || 'Tin nhượng quyền mặt bằng ẩn'} <ExternalLink size={14} className="inline-block ml-1 opacity-50" />
          </Link>
          <div className="flex items-center gap-1.5 text-sm font-medium mt-1" style={{ color: 'var(--text-2)' }}>
            <Store size={14} className="text-gray-400" /> {Franchise.businessTypeName || 'Nhà hàng / Quán ăn'}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusBadge status={item.status} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs" style={{ color: 'var(--text-3)' }}>
        <span className="flex items-center gap-1">
          <MapPin size={12} /> {fullAddr || 'Chưa cập nhật địa chỉ'}
        </span>
        <span className="flex items-center gap-1 font-semibold" style={{ color: 'var(--brand)' }}>
          💰 {formatPrice(Franchise.price)}
        </span>
      </div>

      {item.message && (
        <div className="p-3 mt-2 rounded-lg text-sm bg-gray-50 border border-gray-100 text-gray-600">
          <span className="font-semibold">Lời nhắn của bạn:</span> {item.message}
        </div>
      )}

      <div className="pt-3 mt-1 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-c)' }}>
        <div className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--text-3)' }}>
          <Calendar size={12} /> Đã gửi yêu cầu: {dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}
        </div>
        <Link to={`/nhuong-quyen/${Franchise.slug || Franchise.id || item.entityId}`} className="text-xs font-bold text-orange-500 hover:underline">
          Xem chi tiết tin
        </Link>
      </div>
    </div>
  );
}

export default function OfferedFranchisesPage() {
  const { user } = useAuthStore();
  const { data, isLoading } = useMyFranchiseOffers(user?.id);
  const offers = Array.isArray(data) ? data : data?.data ?? [];
  const total = Array.isArray(data) ? data.length : data?.total ?? 0;

  const stats = {
    total: total,
    accepted: offers.filter((p: any) => p.status === 'accepted').length,
    pending: offers.filter((p: any) => p.status === 'pending').length,
    rejected: offers.filter((p: any) => p.status === 'rejected').length,
  };

  return (
    <div className="max-w-3xl mx-auto pb-12 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Nhượng quyền</div>
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h1 className="section-title mb-1">Tin đã đề nghị</h1>
            <p className="text-sm" style={{ color: 'var(--text-3)' }}>
              Theo dõi trạng thái các đề nghị nhượng quyền của bạn
            </p>
          </div>
          <Link
            to="/nhuong-quyen"
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border hover:border-orange-400 hover:text-orange-500 transition-colors"
            style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}
          >
            <Store size={14} /> Khám phá thêm tin
          </Link>
        </div>
      </div>

      {/* Stats */}
      {offers.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Đã gửi offer', value: stats.total, color: 'var(--text-1)', bg: 'var(--surface-1)' },
            { label: 'Đã đồng ý', value: stats.accepted, color: '#16a34a', bg: '#dcfce7' },
            { label: 'Đang chờ', value: stats.pending, color: '#ca8a04', bg: '#fef9c3' },
            { label: 'Từ chối', value: stats.rejected, color: '#dc2626', bg: '#fee2e2' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-4 text-center" style={{ background: s.bg, border: '1px solid var(--border-c)' }}>
              <p className="text-2xl font-black" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="text-xs mt-1 font-bold" style={{ color: 'var(--text-3)' }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--brand-dim)', borderTopColor: 'var(--brand)' }} />
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-24 rounded-2xl border" style={{ borderColor: 'var(--border-c)', background: 'var(--surface-0)' }}>
          <p className="text-5xl mb-4 text-orange-400 flex justify-center"><Store size={48} /></p>
          <p className="font-bold text-lg mb-2" style={{ color: 'var(--text-1)' }}>
            Bạn chưa gửi đề nghị nào
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
            Tìm các mặt bằng nhượng quyền phù hợp với nhu cầu của bạn
          </p>
          <Link to="/nhuong-quyen" className="btn-brand inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold">
            Khám phá ngay <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((item: any, i: number) => (
            <div key={item.id} className="animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <OfferCard item={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


